import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { handleUserWebsites } from "../axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon, DownloadCloudIcon, TrashIcon } from "lucide-react";
import { convertToIST } from "../utils/utils";
import CustomLoader from "./CustomLoader";
import NoDataComponent from "./NoDataComponent";

const Websites = () => {
  const { token } = useSelector((state) => state.auth);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleGetUsersWebsites = async () => {
    const config = {
      headers: {
        Authorization: "Bearer " + token,
      },
    };

    setLoading(true);
    try {
      const res = await handleUserWebsites(config);

      if (res?.status === 200) {
        // Update to use the 'websites' array from the response
        setUsers(res?.data?.websites || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetUsersWebsites();
  }, []);

  const columns = [
    {
      name: "S.No",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "100px",
    },
    {
      name: "Website",
      selector: (row) => row.siteurl,
      sortable: true,
    },

    {
      name: "Created User Name",
      selector: (row) => row.userName,
      sortable: true,
    },
    {
      name: "Created User Email",
      selector: (row) => row.userEmail,
      sortable: true,
    },
    {
      name: "Created At",
      selector: (row) => convertToIST(row.createdAt),
      sortable: true,
    },
  ];

  // Function to filter users based on search term
  const filteredUsers = users.filter(
    (user) =>
      user.userId.includes(searchTerm) ||
      user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.siteurl.includes(searchTerm)
  );

  const customStyles = {
    headCells: {
      style: {
        borderRight: "1px solid #ddd",
        fontWeight: "bold",
        letterSpacing: "0em",
        alignItems: "center",
      },
    },
    cells: {
      style: {
        borderRight: "1px solid #ddd",
        alignItems: "center",
      },
    },
  };

  const handleDownload = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      "Website,Created User Name,Created User Email,Created At\n" +
      filteredUsers
        .map(
          (row) =>
            `"${row.siteurl}",${row.userName},${row.userEmail},"${convertToIST(
              row.createdAt
            )}"`
        )
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "Websites_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="font-semibold antialiased tracking-normal text-[22px]">
          Websites
        </p>

        <button
          onClick={() => navigate(-1)}
          className="bg-black text-white h-10 w-[130px] rounded-[8px] flex justify-center items-center gap-2 hover:ring-2 hover:ring-gray-300 transition-all duration-300"
        >
          <ArrowLeftIcon />
          <p>Back</p>
        </button>
      </div>
      <div className="my-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="outline-none ring-1 ring-gray-200 2xl:w-[300px] xl:w-[300px] lg:w-[300px] md:w-[300px] w-full h-10 rounded-[8px] indent-3 mt-2 focus:ring-2 focus:ring-indigo-300 transition-all duration-300"
          />
          <button
            onClick={handleDownload}
            className="bg-[#BC3810] text-white h-10 2xl:w-[230px] xl:w-[230px] lg:w-[230px] md:w-[230px] w-full rounded-[8px] flex justify-center items-center gap-2 hover:ring-2 hover:ring-[#f1a791] transition-all duration-300"
          >
            <DownloadCloudIcon />
            <p>Download Report </p>
          </button>
        </div>
      </div>
      <div className="border border-gray-200 mt-2">
        <DataTable
          columns={columns}
          data={filteredUsers}
          pagination
          paginationRowsPerPageOptions={[10, 50, 100, 500]}
          progressPending={loading}
          progressComponent={loading ? <CustomLoader /> : "NoData"}
          customStyles={customStyles}
          noDataComponent={<NoDataComponent />}
          responsive
        />
      </div>
    </div>
  );
};

export default Websites;
